import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Copy, Bot } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";

type Prompt = Database["public"]["Tables"]["prompts"]["Row"];

const Prompts = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [areas, setAreas] = useState<string[]>([]);
  const { refreshSubscriptionStatus } = useAuth();

  // Refresh subscription status only once when component mounts
  useEffect(() => {
    refreshSubscriptionStatus();
  }, [refreshSubscriptionStatus]);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        setIsLoading(true);
        // Fetch prompts
        const { data, error } = await supabase
          .from("prompts")
          .select("*")
          .order("total_uso", { ascending: false });
          
        if (error) throw error;
        setPrompts(data || []);
        
        // Extract unique areas
        const uniqueAreas = [...new Set(data?.map(prompt => prompt.area) || [])];
        setAreas(uniqueAreas);
      } catch (error) {
        console.error("Error fetching prompts:", error);
      } finally {
        // Pequeno atraso para garantir que todos os dados foram processados
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };
    
    fetchPrompts();
  }, []);
  
  // Filtering logic
  const filteredPrompts = prompts.filter(prompt => {
    const matchesTerm = searchTerm === "" || 
      prompt.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.descricao.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesArea = selectedArea === null || prompt.area === selectedArea;
    
    return matchesTerm && matchesArea;
  });
  
  // All users have full access to all prompts
  const displayPrompts = filteredPrompts;
  
  const copyToClipboard = async (prompt: Prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.conteudo);
      
      // Increment usage count
      await supabase.rpc("increment_prompt_use" as any, {
        prompt_id: prompt.id
      });
      
      toast({
        title: "Copiado!",
        description: "O prompt foi copiado para a área de transferência",
      });
    } catch (error) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o prompt",
        variant: "destructive",
      });
    }
  };

  const openChatGPTWithPrompt = async (prompt: Prompt) => {
    try {
      // Copia o prompt para a área de transferência
      await navigator.clipboard.writeText(prompt.conteudo);
      
      // Incrementa o contador de uso
      await supabase.rpc("increment_prompt_use" as any, {
        prompt_id: prompt.id
      });
      
      // Abre o ChatGPT em uma nova aba
      window.open('https://chatgpt.com/', '_blank');
      
      toast({
        title: "Prompt copiado!",
        description: "O prompt foi copiado e o ChatGPT foi aberto. Cole o prompt no chat.",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível copiar o prompt",
        variant: "destructive",
      });
    }
  };

  return (
    <DashboardLayout title="Biblioteca de Prompts" isLoading={isLoading}>
      <div>
        {/* Search and filters - Improved for mobile */}
        <div className="mb-6 flex flex-col space-y-4">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Buscar prompts..."
              className="pl-10 w-full"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex overflow-x-auto pb-2 no-scrollbar -mx-2 px-2">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedArea === null ? "default" : "outline"}
                onClick={() => setSelectedArea(null)}
                className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white hover:text-white dark:text-[#072a45]"
              >
                Todos
              </Button>
              {areas.map(area => (
                <Button
                  key={area}
                  variant={selectedArea === area ? "default" : "outline"}
                  onClick={() => setSelectedArea(area)}
                  className={`${selectedArea === area ? "flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]" : "whitespace-nowrap  dark:text-white"}`}
                >
                  {area}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Prompts list with fixed card heights and aligned buttons */}
        {displayPrompts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayPrompts.map(prompt => (
              <div key={prompt.id} className="border rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow flex flex-col h-full">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg">{prompt.titulo}</h3>
                  <Badge className="flex items-center  bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">{prompt.area}</Badge>
                </div>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-grow">
                  {prompt.descricao}
                </p>
                <div className="flex justify-between items-center mt-auto pt-4">
                  <span className="text-xs text-gray-500">
                    {prompt.total_uso} usos
                  </span>
                  <div className="space-x-2 flex">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openChatGPTWithPrompt(prompt)}
                      className="dark:bg-[#072a45] dark:hover:bg-[#072a45] dark:text-white"
                      title="Copiar prompt e abrir ChatGPT"
                    >
                      <Bot className="h-4 w-4 mr-1" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(prompt)} className=" dark:bg-[#072a45]   dark:hover:bg-[#072a45]  dark:text-white">
                      <Copy className="h-4 w-4 mr-1" /> Copiar
                    </Button>
                    <Button size="sm" asChild className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">
                      <Link to={`/dashboard/prompts/${prompt.id}`}>Ver</Link>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <h3 className="text-lg font-medium">Nenhum prompt encontrado</h3>
            <p className="text-gray-500">
              Tente ajustar seus filtros ou termos de pesquisa
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Prompts;